import { AllSelection, TextSelection } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { Fragment, Node, NodeType, ResolvedPos, Slice } from 'prosemirror-model'

import { findToggleBlockDepth } from 'common/Prose/utils'

export const unwrapList = (editorView: EditorView, type: NodeType) => () => {
  let {
    doc,
    selection,
    selection: { $from, $to, empty, from, to },
    schema: {
      nodes: { list_item, summary, toggle_list },
    },
  } = editorView.state

  let fromToggleBlockDepth: number | null
  if ((fromToggleBlockDepth = findToggleBlockDepth($from))) {
    from = $from.before(-fromToggleBlockDepth)
    $from = doc.resolve(from)
  }

  let toToggleBlockDepth: number | null
  if ((toToggleBlockDepth = findToggleBlockDepth($to))) {
    to = $to.before(-toToggleBlockDepth) + $to.node(-toToggleBlockDepth).nodeSize
    $to = doc.resolve(to)
  }

  // NOTE: all selections are around the doc node, users probably want to go
  // from before the first node to after the last node though, so we
  // "correct" that here
  if (selection instanceof AllSelection) {
    if (doc.nodeSize <= 3) return

    from = 1
    $from = doc.resolve(from)
  }

  // NOTE: if we are on or before a list item, use the position before the list item as start
  let $start: ResolvedPos
  if ($from.node($from.depth - 1)?.type === list_item) {
    $start = doc.resolve($from.before($from.depth - 1))
  } else if (!$from.nodeAfter || $from.nodeAfter.isText) {
    $start = doc.resolve($from.before())
  } else {
    $start = doc.resolve($from.pos)
  }

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const startNode = $start.nodeAfter!
  const startNodeParent = $start.node()
  // TODO: Fix this the next time the file is edited.
  /* eslint-disable no-nested-ternary */
  const startList =
    startNode.type === list_item ? $start.node($start.depth - 1) : startNode.type === toggle_list ? startNode : null
  /* eslint-enable no-nested-ternary */

  let maxAfterPos = $start.pos + startNode.nodeSize
  let nodeFound = false
  let newNodes: Node[] = []

  const tr = editorView.state.tr
  doc.nodesBetween(from, to, (node, position, parent) => {
    // NOTE: ignore all "open" nodes this callback runs through until we find our node at the "from" position
    if (node === startNode) nodeFound = true
    if (!nodeFound) return true

    // NOTE: descend into lists of that type
    if (node.type === type) return true
    // NOTE: only consider nodes on the same level as the start node OR if thats a list item on the same level as that list
    if (
      parent !== startNodeParent &&
      parent !== startList &&
      node.type !== list_item &&
      node.type !== summary &&
      parent.type !== toggle_list
    )
      return false

    if (node.type === list_item || node.type === summary)
      newNodes.push(
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...node.content.content!.map((childNode) =>
          childNode.type.create({ ...childNode.attrs, depth: node.attrs.depth }, childNode.content, childNode.marks),
        ),
      )
    else newNodes.push(node)

    // NOTE: update the position until which we need to delete
    const afterNodePos = position + node.nodeSize
    if (afterNodePos > maxAfterPos) maxAfterPos = afterNodePos

    return false
  })

  tr.replaceRange($start.pos, maxAfterPos, new Slice(Fragment.from(newNodes), 0, 0))

  if (empty) {
    const newSelection = TextSelection.create(
      tr.doc,
      (tr.steps[0] as any).from + (editorView.state.selection.from - editorView.state.selection.$from.before()),
    )
    tr.setSelection(newSelection)
  } else {
    const newNodesCount = newNodes.reduce((count, node) => count + node.nodeSize, 0)

    const newSelection = TextSelection.create(
      tr.doc,
      (tr.steps[0] as any).from,
      (tr.steps[0] as any).from + newNodesCount,
    )
    tr.setSelection(newSelection)
  }

  editorView.dispatch(tr)
  editorView.focus()
}
